import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Avatar, Button, Card, Drawer, Form, Input, Popconfirm, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { AgentItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { RichTextEditor } from '../../components/RichTextEditor';
import { errorMessage } from '../../api/client';
import { useAgents, useCreateAgent, useDeleteAgent, useDomains, useUpdateAgent } from '../../features/email/api';
import { EmptyState, tableLocale, tableLoading } from '../../components/states';
import { palette } from '../../app/theme';

interface AgentForm {
  name: string;
  position?: string | null;
  localPart: string;
  domainId: string;
  signatureHtml?: string | null;
  avatarUrl?: string | null;
  isActive: boolean;
}

export function AgentsPage() {
  const { message } = App.useApp();
  const agents = useAgents();
  const domains = useDomains();
  const create = useCreateAgent();
  const update = useUpdateAgent();
  const remove = useDeleteAgent();
  const [editing, setEditing] = useState<AgentItem | 'new' | null>(null);
  const [form] = Form.useForm<AgentForm>();
  const signature = Form.useWatch('signatureHtml', form);

  const openNew = () => {
    form.resetFields();
    form.setFieldsValue({ isActive: true, domainId: domains.data?.[0]?.id, signatureHtml: '' });
    setEditing('new');
  };
  const openEdit = (a: AgentItem) => {
    form.setFieldsValue({ name: a.name, position: a.position, localPart: a.email.split('@')[0], domainId: a.domainId, signatureHtml: a.signatureHtml ?? '', avatarUrl: a.avatarUrl, isActive: a.isActive });
    setEditing(a);
  };
  const save = async (v: AgentForm) => {
    try {
      if (editing === 'new') await create.mutateAsync(v);
      else if (editing) await update.mutateAsync({ id: editing.id, ...v });
      message.success(editing === 'new' ? 'Agent created' : 'Agent saved');
      setEditing(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Agents"
        subtitle="Personas your team sends as. Each has its own name, title, address on one of your domains, and signature."
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew} disabled={!domains.data?.length}>
            New agent
          </Button>
        }
      />
      <Card>
      {!domains.isLoading && !domains.data?.length && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={<span>Agents send from one of your domains. <Link to="/settings/domains">Add an email domain</Link> first.</span>}
        />
      )}
      <Table<AgentItem>
        rowKey="id"
        loading={tableLoading(agents)}
        locale={tableLocale(
          agents,
          <EmptyState
            description="No agents yet. Create one to send email as a named person."
            action={domains.data?.length ? <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>New agent</Button> : undefined}
          />,
        )}
        dataSource={agents.data ?? []}
        pagination={false}
        columns={[
          {
            title: 'Agent',
            key: 'agent',
            render: (_, a) => (
              <Space size={12}>
                <Avatar size={36} src={a.avatarUrl ?? undefined} style={{ background: palette.primarySoft, color: palette.primary, fontWeight: 600 }}>{a.name[0]?.toUpperCase()}</Avatar>
                <div style={{ lineHeight: 1.35 }}>
                  <a onClick={() => openEdit(a)} style={{ color: palette.text, fontWeight: 600 }}>{a.name}</a>
                  <div><Typography.Text type="secondary" style={{ fontSize: 13 }}>{a.position ?? 'No title'}</Typography.Text></div>
                </div>
              </Space>
            ),
          },
          { title: 'Email', dataIndex: 'email', render: (v, a) => <Space>{v}{!a.domain.verified && <Tag color="warning">Domain unverified</Tag>}</Space> },
          { title: 'Active', dataIndex: 'isActive', width: 90, render: (v: boolean, a) => <Switch size="small" checked={v} onChange={(c) => update.mutateAsync({ id: a.id, isActive: c }).catch((e) => message.error(errorMessage(e)))} /> },
          {
            key: 'actions',
            align: 'right',
            render: (_, a) => (
              <Space>
                <Button size="small" onClick={() => openEdit(a)}>Edit</Button>
                <Popconfirm title={`Delete ${a.name}? Past emails keep their history.`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove.mutateAsync(a.id).then(() => message.success('Agent deleted')).catch((e) => message.error(errorMessage(e)))}>
                  <Button size="small" danger>Delete</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      </Card>
      <Drawer
        title={editing === 'new' ? 'New agent' : `Edit ${editing?.name ?? ''}`}
        open={!!editing}
        onClose={() => setEditing(null)}
        size={560}
        destroyOnHidden
        extra={
          <Space>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="primary" onClick={() => form.submit()} loading={create.isPending || update.isPending}>Save</Button>
          </Space>
        }
      >
        <Form<AgentForm> form={form} layout="vertical" requiredMark={false} onFinish={save}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Sam Seller" />
          </Form.Item>
          <Form.Item name="position" label="Position">
            <Input placeholder="Account Executive" />
          </Form.Item>
          <Form.Item label="Email address" required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="localPart" noStyle rules={[{ required: true, pattern: /^[a-zA-Z0-9._%+-]{1,64}$/, message: 'letters, numbers, dots, hyphens' }]}>
                <Input placeholder="sam" style={{ width: '45%' }} />
              </Form.Item>
              <Input style={{ width: 24, textAlign: 'center', pointerEvents: 'none' }} value="@" readOnly />
              <Form.Item name="domainId" noStyle rules={[{ required: true }]}>
                <Select style={{ width: 'calc(55% - 24px)' }} options={(domains.data ?? []).map((d) => ({ value: d.id, label: d.domain }))} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="avatarUrl" label="Avatar URL">
            <Input placeholder="https://…" />
          </Form.Item>
          <Form.Item label="Signature" extra="Appended to every email this agent sends. Merge tags like {{agent_name}} work here too.">
            <RichTextEditor value={signature ?? ''} onChange={(html) => form.setFieldValue('signatureHtml', html)} minHeight={120} placeholder="Best regards, …" />
          </Form.Item>
          <Form.Item name="signatureHtml" hidden><Input /></Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
